/**
 * WebGL-based asymmetric margin for SDF volumes.
 *
 * Performs morphological dilation/erosion by walking a combined
 * max/min mipmap of the SDF as an implicit octree, pruning subtrees
 * against an asymmetric ellipsoidal kernel. The SDF volume uses
 * hardware trilinear interpolation (LINEAR-filtered 3D texture).
 */

import { ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformBoolean } from '../../../core/utils/uniforms/UniformBoolean';
import { UniformInt } from '../../../core/utils/uniforms/UniformInt';
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import {
    EllipsoidRadii,
    ellipsoidRadiiForMargin,
} from './AsymmetricMarginConvolution';
import { copyTexture3D, createSdfTexture3D } from './Texture3DUtils';
import { maybeYield, resetGlDefaults } from './utils';

/**
 * Builds a combined max/min mipmap into an RG8 destination, one
 * level at a time: R holds the max, G the min over each 2×2×2 child
 * block. The finest level reduces the R8 source SDF; higher levels
 * reduce the previous RG level.
 */
class MipmapBuildShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;

        in vec2 position;

        void main() {
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        out vec4 fragColor;

        uniform sampler3D uSrcTexture;
        uniform int uDstZ;
        // True when reducing the single-channel R8 source SDF (used
        // to build the finest stored level directly from it): sample
        // .r and fold it into both max (R) and min (G). False when
        // reducing the previous RG mipmap level: sample .rg.
        uniform bool uSrcIsR8;
        // Mipmap level the reduction reads from. 0 when uSrcIsR8 (the
        // R8 source has a single level).
        uniform int uSrcLod;

        void main() {
            ivec3 dst = ivec3(
                int(gl_FragCoord.x), int(gl_FragCoord.y), uDstZ
            );

            ivec3 srcBase = dst * 2;
            ivec3 srcSize = textureSize(uSrcTexture, uSrcLod);
            float maxAcc = 0.0;
            float minAcc = 1.0;
            for (int dz = 0; dz < 2; dz++) {
                for (int dy = 0; dy < 2; dy++) {
                    for (int dx = 0; dx < 2; dx++) {
                        ivec3 p = srcBase + ivec3(dx, dy, dz);
                        if (p.x >= srcSize.x ||
                            p.y >= srcSize.y ||
                            p.z >= srcSize.z) {
                            continue;
                        }
                        if (uSrcIsR8) {
                            float v =
                                texelFetch(uSrcTexture, p, uSrcLod).r;
                            maxAcc = max(maxAcc, v);
                            minAcc = min(minAcc, v);
                        } else {
                            vec2 v =
                                texelFetch(uSrcTexture, p, uSrcLod).rg;
                            maxAcc = max(maxAcc, v.r);
                            minAcc = min(minAcc, v.g);
                        }
                    }
                }
            }
            fragColor = vec4(maxAcc, minAcc, 0.0, 1.0);
        }`;

    public readonly uDstZ: UniformInt;
    public readonly uSrcIsR8: UniformBoolean;
    public readonly uSrcLod: UniformInt;

    public constructor() {
        super(
            MipmapBuildShader.VERTEX_CONTENT,
            MipmapBuildShader.FRAGMENT_CONTENT,
            'position'
        );
        this.uDstZ = new UniformInt('uDstZ');
        this.uSrcIsR8 = new UniformBoolean('uSrcIsR8');
        this.uSrcLod = new UniformInt('uSrcLod');

        super.storeUniformLocations(this.uDstZ, this.uSrcIsR8, this.uSrcLod);

        const srcLoc = gl.getUniformLocation(this.programID, 'uSrcTexture');
        this.start();
        gl.uniform1i(srcLoc, 0);
        this.stop();
    }
}

/**
 * Asymmetric-margin shader. Each fragment walks the SDF's max/min
 * mipmap as an implicit octree, depth-first, pruning subtrees
 * against an asymmetric ellipsoidal kernel centred at the output
 * voxel:
 *   - bbox prune: if the closest point of the cell's box is outside
 *     the ellipsoid, skip the subtree;
 *   - value-bound prune: read the cell's max (expand) / min (erode)
 *     at the matching LOD — if it cannot beat best, skip the subtree;
 *   - leaf cells trilerp-sample the source SDF and update best.
 */
class HierarchicalKernelMarginShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;

        in vec2 position;

        void main() {
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        out vec4 fragColor;

        uniform sampler3D uSdfTexture;
        // RG8: R = max over the cell's subtree, G = min. At LOD 0
        // both channels equal the source SDF, so leaf samples use
        // .r regardless of expand/erode mode.
        uniform sampler3D uMipmapTexture;
        uniform int uSliceIdx;
        uniform bool uExpand;
        uniform int uMipLevels;
        uniform vec3 uNegR;
        uniform vec3 uPosR;
        uniform mat4 uVoxelToWorld;
        uniform mat4 uWorldToVoxel;
        uniform vec3 uVolumeSize;

        float ellipsoidE(vec3 p) {
            vec3 r = mix(uNegR, uPosR, step(0.0, p));
            vec3 t = p / max(r, vec3(1e-10));
            return dot(t, t);
        }

        bool bboxOutsideEllipsoid(vec3 centre, vec3 h) {
            vec3 closest = max(abs(centre) - h, vec3(0.0))
                * sign(centre);
            return ellipsoidE(closest) > 1.0;
        }

        // True when the whole cell lies inside the ellipsoid, tested at
        // the farthest corner. Conservative: when a cell spans the
        // origin on an axis the smaller radius is used, so it never
        // reports "inside" for a cell the boundary actually crosses
        // (a false "inside" would skip that cell's surface projection
        // and lose reach).
        bool bboxInsideEllipsoid(vec3 centre, vec3 h) {
            vec3 farAbs = abs(centre) + h;
            vec3 spansOrigin = step(abs(centre), h);
            vec3 sideR = mix(uNegR, uPosR, step(0.0, centre));
            vec3 r = mix(sideR, min(uNegR, uPosR), spansOrigin);
            vec3 t = farAbs / max(r, vec3(1e-10));
            return dot(t, t) <= 1.0;
        }

        // Source SDF value at an integer voxel, clamped to the volume so
        // a central difference at the border falls back to a one-sided
        // step instead of reading outside the texture.
        float sdfTexel(ivec3 p) {
            ivec3 c = clamp(p, ivec3(0), ivec3(uVolumeSize) - ivec3(1));
            return texelFetch(uSdfTexture, c, 0).r;
        }

        void main() {
            ivec3 outputPos = ivec3(
                int(gl_FragCoord.x), int(gl_FragCoord.y), uSliceIdx
            );
            float current = texelFetch(uSdfTexture, outputPos, 0).r;

            vec3 outputVoxelCentre = vec3(outputPos) + 0.5;
            vec3 outputWorld = (
                uVoxelToWorld * vec4(outputVoxelCentre, 1.0)
            ).xyz;

            // Deep inside (expand): dilation can't lower it.
            // Deep outside (contract): erosion can't raise it.
            if (uExpand && current >= 1.0) {
                fragColor = vec4(1.0, 1.0, 1.0, 1.0);
                return;
            }
            if (!uExpand && current <= 0.0) {
                fragColor = vec4(0.0, 0.0, 0.0, 1.0);
                return;
            }

            float best = current;

            // Walk the max-mipmap pyramid as an implicit octree.
            // A cell at LOD L, mipmap-texel position p covers source
            // voxels [p*2^L, (p+1)*2^L) on each axis. Children of
            // (L, p) at LOD L-1 are at positions 2*p + d for
            // d ∈ {0,1}^3 (so 8 children, encoded by 3 bits of k).
            //
            // The voxel→world linear part's column lengths give the
            // per-axis voxel size; mat3(abs(...)) gives a
            // conservative axis-aligned bound on the cell's
            // patient-space half-extent (exact for axis-aligned
            // grids, over-approximate for oblique ones).
            mat3 absLinear = mat3(
                abs(uVoxelToWorld[0].xyz),
                abs(uVoxelToWorld[1].xyz),
                abs(uVoxelToWorld[2].xyz)
            );

            // DFS over the implicit octree. currentPos is the cell's
            // texel position at currentLOD; its per-axis low bits give
            // the child index within the parent, and the parent is
            // currentPos >> 1.
            int depth = 0;
            int currentLOD = uMipLevels - 1;
            ivec3 currentPos = ivec3(0);

            // Safety ceiling, not the real exit condition: the DFS
            // terminates by breaking below once it unwinds past the
            // root. This bound only keeps the loop statically finite,
            // so a logic bug or driver miscompile can never spin
            // forever and hang the GPU. 1<<23 is a round cap set well
            // above the node count a normal traversal visits.
            for (int safety = 0; safety < (1 << 23); safety++) {
                bool subtreePruned = false;

                int cellSize = 1 << currentLOD;
                vec3 cellHalfVoxel = vec3(cellSize) * 0.5;
                // Centre of the current cell in LOD-grid units; at a
                // leaf (cellSize 1) this equals the voxel centre.
                vec3 cellCentre = vec3(currentPos) + 0.5;
                vec3 cellCentreVoxel = cellCentre * float(cellSize);
                vec3 cellCentreWorld = (
                    uVoxelToWorld * vec4(cellCentreVoxel, 1.0)
                ).xyz;
                vec3 cellOffsetMm = uExpand
                    ? (outputWorld - cellCentreWorld)
                    : (cellCentreWorld - outputWorld);
                vec3 cellHalfExtentMm = absLinear * cellHalfVoxel;

                // Texel does not touch ellipsoid: prune the subtree.
                if (bboxOutsideEllipsoid(
                        cellOffsetMm, cellHalfExtentMm)) {
                    subtreePruned = true;
                // Texel completely outside the volume: prune the subtree.
                } else if (any(greaterThanEqual(
                        vec3(currentPos * cellSize), uVolumeSize))) {
                    subtreePruned = true;
                } else if (currentLOD == 0) {
                    // Leaf: one voxel admitted by the bbox prune. Fold
                    // up to two samples into best:
                    //   1. the voxel CENTRE, only when it lies in the
                    //      kernel (E <= 1). Sampling a centre that is
                    //      outside would read beyond radius r and
                    //      over-apply. This preserves the interior value
                    //      (e.g. the saturated peak of a thin
                    //      structure), which the surface sample alone
                    //      can step off of.
                    //   2. the radial projection of the offset onto the
                    //      ellipsoid surface (clamped into the cell),
                    //      only when the cell is NOT fully inside — i.e.
                    //      the boundary passes through it. This gives the
                    //      kernel its exact, sub-voxel reach without ever
                    //      sampling beyond the surface. Fully-interior
                    //      cells skip it: the centre already covers them.
                    // Sampling uSdfTexture (the current pass input) keeps
                    // multi-pass margins (dilate then erode) chained.
                    float ellE = ellipsoidE(cellOffsetMm);

                    // Voxel centre within ellipsoid kernel: sample it.
                    if (ellE <= 1.0) {
                        float vCtr = texture(
                            uSdfTexture, cellCentre / uVolumeSize
                        ).r;
                        if (uExpand) {
                            if (vCtr > best) { best = vCtr; }
                        } else {
                            if (vCtr < best) { best = vCtr; }
                        }
                    }
                    // Voxel intersects ellipsoid boundary: sample the ellipsoid projection.
                    if (!bboxInsideEllipsoid(
                            cellOffsetMm, cellHalfExtentMm)) {
                        float scale =
                            ellE > 1e-6 ? inversesqrt(ellE) : 1.0;
                        vec3 projOffset = cellOffsetMm * scale;
                        vec3 sampleWorld = uExpand
                            ? (outputWorld - projOffset)
                            : (outputWorld + projOffset);
                        vec3 sampleVoxel =
                            (uWorldToVoxel * vec4(sampleWorld, 1.0)).xyz;

                        // The projection can land outside this voxel;
                        // clamp it into the voxel so the read stays on
                        // this cell's own data and never reaches past
                        // the kernel or out of the volume.
                        sampleVoxel = clamp(
                            sampleVoxel,
                            cellCentre - 0.5,
                            cellCentre + 0.5
                        );
                        float vProjected = texture(
                            uSdfTexture, sampleVoxel / uVolumeSize
                        ).r;
                        if (uExpand) {
                            if (vProjected > best) { best = vProjected; }
                        } else {
                            if (vProjected < best) { best = vProjected; }
                        }
                    }
                    if (uExpand) {
                        if (best >= 1.0) { break; }
                    } else {
                        if (best <= 0.0) { break; }
                    }
                } else if (uExpand) {
                    // Algorithm LOD L is stored at mipmap level L-1.
                    float cellMax = texelFetch(
                        uMipmapTexture, currentPos, currentLOD - 1
                    ).r;
                    if (cellMax <= best) {
                        subtreePruned = true;
                    }
                } else {
                    float cellMin = texelFetch(
                        uMipmapTexture, currentPos, currentLOD - 1
                    ).g;
                    if (cellMin >= best) {
                        subtreePruned = true;
                    }
                }

                if (!subtreePruned && currentLOD > 0) {
                    // Descend to child 0 (currentPos * 2).
                    depth = depth + 1;
                    currentLOD = currentLOD - 1;
                    currentPos = currentPos * 2;
                } else {
                    // Advance to the next un-visited sibling, unwinding
                    // completed subtrees. The child index is currentPos's
                    // per-axis low bits; the parent is currentPos >> 1.
                    bool foundSibling = false;
                    while (depth > 0) {
                        int curK = (currentPos.x & 1)
                            | ((currentPos.y & 1) << 1)
                            | ((currentPos.z & 1) << 2);
                        if (curK < 7) {
                            int nk = curK + 1;
                            ivec3 parent = currentPos >> 1;
                            currentPos = parent * 2 + ivec3(
                                nk & 1,
                                (nk >> 1) & 1,
                                (nk >> 2) & 1
                            );
                            foundSibling = true;
                            break;
                        }
                        // All 8 children visited: ascend to the parent.
                        currentPos = currentPos >> 1;
                        depth = depth - 1;
                        currentLOD = currentLOD + 1;
                    }
                    if (!foundSibling) { break; }
                }
            }

            // Sub-voxel reach for the output voxel's own cell: applied
            // only when every radius is below half the voxel size, so the
            // ellipsoid fits inside one voxel and the octree reaches no
            // neighbour. The SDF gradient aims a real source read at the
            // ellipsoid's support point toward the interior (expand) /
            // exterior (erode); the sampled source value is folded into
            // best. The support point lies on the ellipsoid, so O ∓ b* is
            // a valid kernel sample and only ever a lower bound on the
            // result.
            vec3 voxelSize = vec3(
                length(uVoxelToWorld[0].xyz),
                length(uVoxelToWorld[1].xyz),
                length(uVoxelToWorld[2].xyz)
            );
            if (all(lessThan(max(uNegR, uPosR), 0.5 * voxelSize))) {
                vec3 gradVoxel = 0.5 * vec3(
                    sdfTexel(outputPos + ivec3(1, 0, 0))
                        - sdfTexel(outputPos - ivec3(1, 0, 0)),
                    sdfTexel(outputPos + ivec3(0, 1, 0))
                        - sdfTexel(outputPos - ivec3(0, 1, 0)),
                    sdfTexel(outputPos + ivec3(0, 0, 1))
                        - sdfTexel(outputPos - ivec3(0, 0, 1))
                );
                vec3 gWorld = transpose(mat3(uWorldToVoxel)) * gradVoxel;
                // Support point of the asymmetric ellipsoid in the
                // direction that moves the sample toward the interior
                // (expand) / exterior (erode) — both are -gradient.
                // Per-octant radii by its sign.
                vec3 vDir = -gWorld;
                vec3 sr = mix(uNegR, uPosR, step(0.0, vDir));
                vec3 num = sr * sr * vDir;
                float den = sqrt(dot(num, vDir));
                if (den > 1e-12) {
                    vec3 bStar = num / den;
                    vec3 sampleWorld = uExpand
                        ? (outputWorld - bStar)
                        : (outputWorld + bStar);
                    vec3 sampleVoxel =
                        (uWorldToVoxel * vec4(sampleWorld, 1.0)).xyz;
                    float vGrad =
                        texture(uSdfTexture, sampleVoxel / uVolumeSize).r;
                    if (uExpand) {
                        if (vGrad > best) { best = vGrad; }
                    } else {
                        if (vGrad < best) { best = vGrad; }
                    }
                }
            }

            fragColor = vec4(best, best, best, 1.0);
        }`;

    public readonly uSliceIdx: UniformInt;
    public readonly uExpand: UniformBoolean;
    public readonly uMipLevels: UniformInt;
    public readonly uNegR: UniformVec3;
    public readonly uPosR: UniformVec3;
    public readonly uVoxelToWorld: UniformMatrix;
    public readonly uWorldToVoxel: UniformMatrix;
    public readonly uVolumeSize: UniformVec3;

    public constructor() {
        super(
            HierarchicalKernelMarginShader.VERTEX_CONTENT,
            HierarchicalKernelMarginShader.FRAGMENT_CONTENT,
            'position'
        );
        this.uSliceIdx = new UniformInt('uSliceIdx');
        this.uExpand = new UniformBoolean('uExpand');
        this.uMipLevels = new UniformInt('uMipLevels');
        this.uNegR = new UniformVec3('uNegR');
        this.uPosR = new UniformVec3('uPosR');
        this.uVoxelToWorld = new UniformMatrix('uVoxelToWorld');
        this.uWorldToVoxel = new UniformMatrix('uWorldToVoxel');
        this.uVolumeSize = new UniformVec3('uVolumeSize');

        super.storeUniformLocations(
            this.uSliceIdx,
            this.uExpand,
            this.uMipLevels,
            this.uNegR,
            this.uPosR,
            this.uVoxelToWorld,
            this.uWorldToVoxel,
            this.uVolumeSize
        );

        const sdfLoc = gl.getUniformLocation(this.programID, 'uSdfTexture');
        const mipmapLoc = gl.getUniformLocation(
            this.programID,
            'uMipmapTexture'
        );
        this.start();
        gl.uniform1i(sdfLoc, 0);
        gl.uniform1i(mipmapLoc, 1);
        this.stop();
    }
}

interface MipmapBundle {
    texture: WebGLTexture;
    // Algorithm LOD count (LOD 0 = full-resolution source). The
    // texture stores LODs 1.., i.e. levels-1 GL mip levels.
    levels: number;
}

interface CachedMipmaps {
    sdfHandle: WebGLTexture;
    mipmap: MipmapBundle;
}

/**
 * Renders asymmetric margins on SDF volumes: builds and caches the
 * SDF's max/min mipmap, then runs the octree-traversal shader
 * slice-by-slice into the output texture.
 */
export class AsymmetricMarginRenderer {
    private hierarchicalShader: HierarchicalKernelMarginShader;
    private mipmapShader: MipmapBuildShader;
    private cachedMipmaps: CachedMipmaps | null = null;

    public constructor() {
        this.hierarchicalShader = new HierarchicalKernelMarginShader();
        this.mipmapShader = new MipmapBuildShader();
    }

    /** Dispose the prepared mipmap, if any. */
    public release(): void {
        if (this.cachedMipmaps) {
            gl.deleteTexture(this.cachedMipmaps.mipmap.texture);
            this.cachedMipmaps = null;
        }
    }

    /**
     * Return the cached combined max+min mipmap for `sdfTexture`,
     * building a fresh one if no slot exists or the previous slot
     * was for a different SDF.
     */
    private getOrBuildMipmaps(
        sdfTexture: WebGLTexture,
        nx: number,
        ny: number,
        nz: number
    ): CachedMipmaps {
        if (this.cachedMipmaps && this.cachedMipmaps.sdfHandle === sdfTexture) {
            return this.cachedMipmaps;
        }
        if (this.cachedMipmaps) {
            gl.deleteTexture(this.cachedMipmaps.mipmap.texture);
        }
        const mipmap = this.buildMipmap(sdfTexture, nx, ny, nz);
        this.cachedMipmaps = { sdfHandle: sdfTexture, mipmap };
        return this.cachedMipmaps;
    }

    /**
     * Build the combined max/min RG8 pyramid of `srcTexture` (R8 3D)
     * that the traversal uses to bound and prune octree subtrees:
     * R = per-cell max, G = min. Caller owns and must delete it.
     *
     * Level 0 is half the source resolution (one RG texel per 2x2x2
     * source block, reduced from the R8 source); each higher level
     * reduces the level below. Stored level k holds algorithm LOD
     * k+1, so the bundle's `levels` is one greater than the GL level
     * count. The traversal samples LOD >= 1 only.
     *
     * Levels >=1 reduce the previous level in place; TEXTURE_MAX_LEVEL
     * is pinned to L-1 so the render-target level stays outside the
     * sampleable range (ANGLE rejects an in-range colour attachment as
     * a feedback loop).
     */
    private buildMipmap(
        srcTexture: WebGLTexture,
        nx: number,
        ny: number,
        nz: number
    ): MipmapBundle {
        const pnx = 1 << Math.ceil(Math.log2(Math.max(nx, 1)));
        const pny = 1 << Math.ceil(Math.log2(Math.max(ny, 1)));
        const pnz = 1 << Math.ceil(Math.log2(Math.max(nz, 1)));
        const pmaxDim = Math.max(pnx, pny, pnz);
        // Algorithm LOD count over the power-of-two cube (LOD 0 is the
        // full-resolution source). The traversal walks these LODs.
        const levels = Math.max(1, Math.log2(pmaxDim) + 1);

        // The stored texture begins at LOD 1 (half resolution), so its
        // dimensions and GL level count are one step down from the
        // algorithm's.
        const mnx = Math.max(1, pnx >> 1);
        const mny = Math.max(1, pny >> 1);
        const mnz = Math.max(1, pnz >> 1);
        const storedLevels = Math.max(1, levels - 1);

        const mipTex = gl.createTexture();
        if (!mipTex) {
            throw new Error('AsymmetricMarginGL: failed to allocate mipmap');
        }
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, mipTex);
        gl.texStorage3D(gl.TEXTURE_3D, storedLevels, gl.RG8, mnx, mny, mnz);
        gl.texParameteri(
            gl.TEXTURE_3D,
            gl.TEXTURE_MIN_FILTER,
            gl.NEAREST_MIPMAP_NEAREST
        );
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);

        const fbo = gl.createFramebuffer();
        if (!fbo) {
            gl.deleteTexture(mipTex);
            throw new Error('AsymmetricMarginGL: failed to allocate fbo');
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);

        gl.disable(gl.BLEND);
        this.mipmapShader.start();
        RenderStorage.getQuadVAO().bind(0);

        // Stored level 0 (algorithm LOD 1): 2×2×2 max/min reduction of
        // the R8 source. Rendering the full padded level lets the
        // bounds check write (max=0, min=1) into padding cells outside
        // the source — neutral values that never raise a parent's max
        // or lower its min.
        this.mipmapShader.uSrcIsR8.loadBoolean(true);
        this.mipmapShader.uSrcLod.loadInt(0);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, srcTexture);
        gl.viewport(0, 0, mnx, mny);
        for (let z = 0; z < mnz; z++) {
            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                mipTex,
                0,
                z
            );
            this.mipmapShader.uDstZ.loadInt(z);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }

        // Higher levels reduce the previous level of mipTex in place.
        // Pinning TEXTURE_MAX_LEVEL to L-1 keeps the render-target
        // level L outside the sampleable range, so no feedback loop is
        // formed and no scratch copy is needed.
        this.mipmapShader.uSrcIsR8.loadBoolean(false);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, mipTex);

        for (let L = 1; L < storedLevels; L++) {
            const lnx = Math.max(1, mnx >> L);
            const lny = Math.max(1, mny >> L);
            const lnz = Math.max(1, mnz >> L);

            gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, L - 1);
            this.mipmapShader.uSrcLod.loadInt(L - 1);

            gl.viewport(0, 0, lnx, lny);
            for (let z = 0; z < lnz; z++) {
                gl.framebufferTextureLayer(
                    gl.FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT0,
                    mipTex,
                    L,
                    z
                );
                this.mipmapShader.uDstZ.loadInt(z);
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
        }

        // Restore the full mip range for the consumer (the octree
        // shader samples every level via textureLod/texelFetch).
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, storedLevels - 1);

        RenderStorage.getQuadVAO().unbind(0);
        this.mipmapShader.stop();
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fbo);
        resetGlDefaults();

        return { texture: mipTex, levels };
    }

    /**
     * Apply asymmetric margin by walking the max-mipmap pyramid as
     * an implicit octree. The shader determines cell geometry from
     * the mipmap LOD and texel position; the ellipsoid radii are
     * the only kernel parameters uploaded per pass.
     *
     * Writes the result into `output`'s SDF texture directly. No
     * CPU readback. Caller owns the output and is responsible for
     * invalidating its cached pixel state if needed.
     */
    public async applyMarginHierarchical(
        src: ContourTexture,
        expansion: EllipsoidRadii,
        contraction: EllipsoidRadii,
        output: ContourTexture,
        signal?: AbortSignal
    ): Promise<void> {
        if (!src.signedDistanceField) {
            throw new Error('AsymmetricMarginGL: source has no SDF');
        }
        const sdf = src.signedDistanceField;
        const outSdf = output.signedDistanceField;
        const nx = sdf.size[0];
        const ny = sdf.size[1];
        const nz = sdf.size[2];
        if (nx === 0 || ny === 0 || nz === 0) {
            return;
        }
        if (
            outSdf.size[0] !== nx ||
            outSdf.size[1] !== ny ||
            outSdf.size[2] !== nz
        ) {
            throw new Error(
                'AsymmetricMarginGL: output size does not match source'
            );
        }

        const passes: { radii: EllipsoidRadii; expand: boolean }[] = [];
        if (!isZeroRadii(expansion)) {
            passes.push({ radii: expansion, expand: true });
        }
        if (!isZeroRadii(contraction)) {
            passes.push({ radii: contraction, expand: false });
        }
        if (passes.length === 0) {
            copyTexture3D(sdf.texture, outSdf.texture, nx, ny, nz);
            output.invalidateCachedPixels();
            return;
        }

        // Only intermediate passes need a temp texture; the last pass
        // writes directly into `output`. With 1 pass: zero temps.
        let intermediate: WebGLTexture | null = null;
        if (passes.length > 1) {
            intermediate = createSdfTexture3D(nx, ny, nz);
            if (!intermediate) {
                throw new Error(
                    'AsymmetricMarginGL: intermediate texture alloc failed'
                );
            }
        }

        const grid = src.textureGrid;
        const voxelToWorld = grid.voxelToWorld;
        const worldToVoxel = grid.worldToVoxel;

        const { mipmap } = this.getOrBuildMipmaps(sdf.texture, nx, ny, nz);
        const volumeSize = vec3.fromValues(nx, ny, nz);

        let lastYield = performance.now();
        let current: WebGLTexture = sdf.texture;
        const fbo = new FrameBufferObject(vec2.fromValues(nx, ny));

        try {
            for (let passIdx = 0; passIdx < passes.length; passIdx++) {
                const pass = passes[passIdx];
                const isLast = passIdx === passes.length - 1;
                const dst: WebGLTexture = isLast
                    ? outSdf.texture
                    : intermediate!;

                gl.disable(gl.BLEND);
                this.hierarchicalShader.start();
                gl.viewport(0, 0, nx, ny);
                RenderStorage.getQuadVAO().bind(0);

                this.hierarchicalShader.uExpand.loadBoolean(pass.expand);
                this.hierarchicalShader.uMipLevels.loadInt(mipmap.levels);
                this.hierarchicalShader.uNegR.loadVec3(pass.radii.negR);
                this.hierarchicalShader.uPosR.loadVec3(pass.radii.posR);
                this.hierarchicalShader.uVoxelToWorld.loadMatrix(voxelToWorld);
                this.hierarchicalShader.uWorldToVoxel.loadMatrix(worldToVoxel);
                this.hierarchicalShader.uVolumeSize.loadVec3(volumeSize);

                gl.activeTexture(gl.TEXTURE0);
                gl.bindTexture(gl.TEXTURE_3D, current);
                gl.activeTexture(gl.TEXTURE1);
                gl.bindTexture(gl.TEXTURE_3D, mipmap.texture);

                for (let z = 0; z < nz; z++) {
                    if (signal?.aborted) {
                        throw new DOMException(
                            'Operation aborted',
                            'AbortError'
                        );
                    }
                    this.hierarchicalShader.uSliceIdx.loadInt(z);
                    fbo.bind();
                    fbo.createTextureLayer(dst, z);
                    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                }

                current = dst;

                RenderStorage.getQuadVAO().unbind(0);
                this.hierarchicalShader.stop();
                fbo.unbind();
                resetGlDefaults();

                lastYield = await maybeYield(lastYield, signal);
            }
        } finally {
            fbo.delete();
            if (intermediate) {
                gl.deleteTexture(intermediate);
            }
        }

        output.invalidateCachedPixels();
    }

    public delete(): void {
        this.release();
        this.hierarchicalShader.delete();
        this.mipmapShader.delete();
    }
}

function isZeroRadii(radii: EllipsoidRadii): boolean {
    return (
        radii.negR[0] === 0 &&
        radii.negR[1] === 0 &&
        radii.negR[2] === 0 &&
        radii.posR[0] === 0 &&
        radii.posR[1] === 0 &&
        radii.posR[2] === 0
    );
}

let asymmetricMarginRenderer: AsymmetricMarginRenderer | null = null;

/**
 * GPU asymmetric margin via DFS over the max-mipmap pyramid. The
 * renderer caches the max/min mipmaps for the last-seen SDF texture
 * handle, so repeated calls with the same `src` (i.e. same SDF
 * texture identity) skip the mipmap rebuild and run only the
 * shader passes.
 */
export async function applyAsymmetricMarginGpuHierarchical(
    src: ContourTexture,
    negMarginMm: ReadonlyVec3,
    posMarginMm: ReadonlyVec3,
    output: ContourTexture,
    signal?: AbortSignal
): Promise<void> {
    if (!asymmetricMarginRenderer) {
        asymmetricMarginRenderer = new AsymmetricMarginRenderer();
    }
    const { expansion, contraction } = ellipsoidRadiiForMargin(
        negMarginMm,
        posMarginMm
    );
    await asymmetricMarginRenderer.applyMarginHierarchical(
        src,
        expansion,
        contraction,
        output,
        signal
    );
}

/** Release the cached mipmaps held by the asymmetric-margin renderer. */
export function releaseAsymmetricMarginGpu(): void {
    asymmetricMarginRenderer?.release();
}
